/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Chip, Tooltip, Typography, makeStyles } from '@material-ui/core';
import SecurityIcon from '@material-ui/icons/Security';
import WarningIcon from '@material-ui/icons/Warning';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {
  ServiceNode,
  hasTraefikLockdown,
  isTrulyOpen,
  policyState,
} from './types';

// Stable team -> hue mapping so colors survive re-renders and node reorders.
export const teamColor = (team: string | null): string => {
  if (!team) return '#9e9e9e';
  let hash = 0;
  for (let i = 0; i < team.length; i += 1) {
    hash = (hash * 31 + team.charCodeAt(i)) % 360;
  }
  return `hsl(${hash}, 55%, 45%)`;
};

const useStyles = makeStyles(theme => ({
  node: {
    borderRadius: 6,
    background: theme.palette.background.paper,
    minWidth: 180,
    minHeight: 64,
    boxShadow: theme.shadows[2],
    overflow: 'visible',
    position: 'relative',
  },
  locked: { border: `2px solid ${theme.palette.success.main}` },
  partial: { border: `2px solid ${theme.palette.warning.main}` },
  open: { border: `2px dashed ${theme.palette.text.disabled}` },
  ghost: {
    border: `2px dotted ${theme.palette.text.disabled}`,
    opacity: 0.6,
  },
  selected: { boxShadow: `0 0 0 3px ${theme.palette.primary.main}` },
  strip: { height: 5 },
  body: {
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  name: { fontWeight: 600, flexGrow: 1, lineHeight: 1.2 },
  chip: { height: 18, fontSize: 10 },
  traefikDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: theme.palette.info.main,
    flexShrink: 0,
  },
}));

export type ServiceNodeData = {
  service: ServiceNode;
  ghost?: boolean;
  selected?: boolean;
  stagedEnable?: boolean;
};

// Memoized: edges draw ELK-routed paths and node data objects keep their
// identity across selection changes, so a click re-renders only the node
// gaining and the node losing the highlight instead of the whole graph.
export const ServiceFlowNode = React.memo(({ data }: { data: ServiceNodeData }) => {
  const classes = useStyles();
  const { service, ghost } = data;
  const state = policyState(service);
  const unprotected = service.envs.filter(e => !service.policyEnabled[e]);
  const traefik = hasTraefikLockdown(service);

  const stateEl = (() => {
    if (state === 'locked' || data.stagedEnable) {
      return (
        <Tooltip title="Peer allow-list (calledBy) in every environment">
          <SecurityIcon style={{ fontSize: 16 }} htmlColor="#4caf50" />
        </Tooltip>
      );
    }
    if (state === 'partial') {
      return (
        <Tooltip title={`No peer allow-list in: ${unprotected.join(', ')}`}>
          <WarningIcon style={{ fontSize: 16 }} htmlColor="#ff9800" />
        </Tooltip>
      );
    }
    return (
      <Chip
        className={classes.chip}
        label={isTrulyOpen(service) ? 'allow-all' : 'no peers'}
        variant="outlined"
      />
    );
  })();

  const stateClasses = {
    locked: classes.locked,
    partial: classes.partial,
    open: classes.open,
  } as const;
  const stateClass = ghost ? classes.ghost : stateClasses[state];

  return (
    <div
      className={`${classes.node} ${stateClass} ${data.selected ? classes.selected : ''}`}
    >
      {/* One invisible handle per side: ELK routes the visible paths, React
          Flow just needs a source/target anchor to consider edges valid. */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ opacity: 0, top: '50%', left: 0 }}
      />
      <div
        className={classes.strip}
        style={{ background: teamColor(service.team) }}
      />
      <div className={classes.body}>
        <Typography variant="body2" className={classes.name}>
          {service.name}
        </Typography>
        {ghost ? (
          <Tooltip title={`Namespace ${service.namespace} is not a catalog service`}>
            <HelpOutlineIcon style={{ fontSize: 16 }} color="disabled" />
          </Tooltip>
        ) : (
          <>
            {stateEl}
            {traefik && (
              <Tooltip title="Chart autoLockdown: Traefik + baseline admitted; peer callers still need calledBy.">
                <span className={classes.traefikDot} aria-label="Traefik lockdown" />
              </Tooltip>
            )}
          </>
        )}
        {service.fetchError && (
          <Tooltip title={`Values fetch failed: ${service.fetchError}`}>
            <WarningIcon style={{ fontSize: 16 }} color="error" />
          </Tooltip>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        style={{ opacity: 0, top: '50%', right: 0 }}
      />
    </div>
  );
});
